import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// Fetch item-level packing data for a sales order (for Create Package flow)
export const getPackingItems = createAsyncThunk(
  "packingSummary/getPackingItems",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.SALES_ORDER.GET_PACKING_ITEMS(salesOrderId));
      // API returns { salesOrderId, salesOrderNumber, items: [...] }
      // Return the full response data to preserve salesOrderNumber
      return {
        salesOrderId: data.data?.salesOrderId,
        salesOrderNumber: data.data?.salesOrderNumber,
        items: data.data?.items || [],
      };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Create a new package for a sales order with items
export const createPackage = createAsyncThunk(
  "packingSummary/createPackage",
  async (payload, { rejectWithValue }) => {
    try {
      // payload: { salesOrderId, items: [{ salesOrderItemId, itemId, qtyToPack }] }
      const { data } = await axios.post(API_URL.PACKAGE.CREATE, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Fetch pending sales orders for packaging (Pending Orders tab)
export const getPendingPackageSalesOrders = createAsyncThunk(
  "packingSummary/getPendingPackageSalesOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setPendingPackageSOActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.PACKAGE.PENDING_SALES_ORDERS}?${queryString}`
      );
      const pending_package_so_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { pending_package_so_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Fetch all packages (for list page)
export const getPackages = createAsyncThunk(
  "packingSummary/getPackages",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(`${API_URL.PACKAGE.LIST}?${queryString}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Fetch full package details (for details panel)
export const getPackageFull = createAsyncThunk(
  "packingSummary/getPackageFull",
  async (packageId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.PACKAGE.FULL(packageId));
      return data.data || data;
    } catch (error) {
      return rejectWithValue(error.data || error);
    }
  }
);

// Cancel a Packed package. Reason is required (max 500 chars, validated server-side too).
// On success the parent SO PackageStatus is recomputed by the backend; the UI must invalidate
// its cached list so the cancelled package shows the new status (caller sets needsRefreshRef).
export const cancelPackage = createAsyncThunk(
  "packingSummary/cancelPackage",
  async ({ packageId, reason }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.PACKAGE.CANCEL(packageId), { reason });
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Fetch package-eligible sales orders (for SO selection in Create Package)
export const getPackageEligibleSalesOrders = createAsyncThunk(
  "packingSummary/getPackageEligibleSalesOrders",
  async (searchText = "", { rejectWithValue }) => {
    try {
      const params = searchText ? { searchText } : {};
      const { data } = await axios.get(
        API_URL.SALES_ORDER.PACKAGE_ELIGIBLE,
        { params }
      );
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const initialState = {
  package_list: [],
  package_list_pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  // Full package details for details panel
  package_detail: null,
  package_detail_loading: false,
  package_detail_error: null,
  // Item-level packing data for Create Package flow
  packing_items: [],
  packing_items_sales_order_number: null, // Sales Order Number from packing-items API
  packing_items_loading: false,
  // Loading states
  list_loading: false,
  create_loading: false,
  // Package-eligible sales orders for SO selection dropdown
  package_eligible_sales_orders: [],
  package_eligible_sales_orders_loading: false,
  pending_package_so_list: [],
  pending_package_so_total_records: 0,
  pending_package_so_active_payload: null,
};

const packingSummarySlice = createSlice({
  name: "packingSummary",
  initialState,
  reducers: {
    resetPackageList(state) {
      state.package_list = [];
      state.package_list_pagination = {
        current: 1,
        pageSize: 10,
        total: 0,
      };
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetPackingItems(state) {
      state.packing_items = [];
      state.packing_items_sales_order_number = null;
    },
    resetPackageDetail(state) {
      state.package_detail = null;
      state.package_detail_loading = false;
      state.package_detail_error = null;
    },
    resetPackageEligibleSalesOrders(state) {
      state.package_eligible_sales_orders = [];
      state.package_eligible_sales_orders_loading = false;
    },
    setPendingPackageSOActivePayload(state, action) {
      state.pending_package_so_active_payload = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create package
      .addCase(createPackage.pending, (state) => {
        state.create_loading = true;
      })
      .addCase(createPackage.fulfilled, (state) => {
        state.create_loading = false;
      })
      .addCase(createPackage.rejected, (state) => {
        state.create_loading = false;
      })
      // Get packages list
      .addCase(getPackages.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(getPackages.fulfilled, (state, action) => {
        // API returns { data: { list: [...], totalRecords, pageNumber, pageSize, ... }, isSuccess, message }
        const responseData = action.payload?.data || {};
        state.package_list = responseData.list || [];
        state.package_list_pagination = {
          current: responseData.pageNumber || 1,
          pageSize: responseData.pageSize || 10,
          total: responseData.totalRecords || 0,
        };
        state.list_loading = false;
      })
      .addCase(getPackages.rejected, (state) => {
        state.list_loading = false;
      })
      // Get packing items (item-level data for Start Packing)
      .addCase(getPackingItems.pending, (state) => {
        state.packing_items_loading = true;
      })
      .addCase(getPackingItems.fulfilled, (state, action) => {
        state.packing_items = action.payload?.items || [];
        state.packing_items_sales_order_number = action.payload?.salesOrderNumber || null;
        state.packing_items_loading = false;
      })
      .addCase(getPackingItems.rejected, (state) => {
        state.packing_items_loading = false;
      })
      // Get full package details
      .addCase(getPackageFull.pending, (state) => {
        state.package_detail_loading = true;
        state.package_detail_error = null;
      })
      .addCase(getPackageFull.fulfilled, (state, action) => {
        state.package_detail = action.payload;
        state.package_detail_loading = false;
        state.package_detail_error = null;
      })
      .addCase(getPackageFull.rejected, (state, action) => {
        state.package_detail_loading = false;
        state.package_detail_error = action.payload || "Failed to load package details";
      })
      // Get package-eligible sales orders
      .addCase(getPackageEligibleSalesOrders.pending, (state) => {
        state.package_eligible_sales_orders_loading = true;
      })
      .addCase(getPackageEligibleSalesOrders.fulfilled, (state, action) => {
        state.package_eligible_sales_orders = action.payload || [];
        state.package_eligible_sales_orders_loading = false;
      })
      .addCase(getPackageEligibleSalesOrders.rejected, (state) => {
        state.package_eligible_sales_orders = [];
        state.package_eligible_sales_orders_loading = false;
      })
      // Get pending package sales orders
      .addCase(getPendingPackageSalesOrders.fulfilled, (state, action) => {
        state.pending_package_so_list = action.payload.pending_package_so_list;
        state.pending_package_so_total_records = action.payload.data?.totalRecords || 0;
      });
  },
});

export const { resetPackageList, resetPackingItems, resetPackageDetail, setActivePayload, resetPackageEligibleSalesOrders, setPendingPackageSOActivePayload } = packingSummarySlice.actions;

export default packingSummarySlice.reducer;
